/**
 * Geçerlik kuralları.
 *
 * DİKKAT: Bu eşikler veritabanındaki `compute_trial_rt()` tetikleyicisiyle
 * AYNI olmak zorunda (packages/db/sql/0003_guards.sql). İkisi birbirinden
 * ayrı düşerse istemci bir denemeyi geçerli sayarken veritabanı geçersiz
 * sayar ve eşik hesabı sessizce bozulur.
 *
 * Tek doğruluk kaynağı burası; SQL tarafındaki sayılar bu dosyadan
 * kopyalanmıştır ve değiştirilirse ikisi birlikte değiştirilir.
 */

import type { InvalidReason, TrialObservation, TrialSpec, Outcome } from './types'

/** Altında insan tepkisi olamaz; tahmin veya rastgele tıklamadır */
export const MIN_PLAUSIBLE_RT_MS = 120

/**
 * Sunumda kaybedilen süre eşiği.
 *
 * Burada KARE SAYISINA bakmıyoruz, KAYBEDİLEN SÜREYE bakıyoruz. Sebebi
 * gerçek donanımda ortaya çıktı: 178 Hz bir ekranda üç kare atlamak 17 ms,
 * yani 60 Hz'de tek karelik bir gecikme kadar — zararsız. Aynı üç kare
 * 60 Hz'de 50 ms eder ve ölçümü bozar. Kare sayısına bakan bir kural
 * yüksek yenileme hızlı ekranlarda sağlam denemeleri boşuna çöpe atar.
 */
export const MAX_PRESENTATION_LAG_MS = 50

/** Yenileme hızı bilinmiyorsa varsayılan kare süresi */
export const ASSUMED_FRAME_MS = 16.7

/**
 * Kare gözcüsü eşiği: bu süre boyunca hiç kare gelmezse sunum durmuş
 * sayılır ve oturum duraklatılır.
 *
 * Gerçek tarayıcıda ölçüldü: `visibilityState` "visible" iken rAF sekiz
 * saniye boyunca hiç tetiklenmedi. Görünürlük bayrağı tek başına yetmiyor.
 */
export const STALL_TIMEOUT_MS = 1000

/**
 * Geriye dönük: kare sayısı cinsinden karşılık (60 Hz varsayımıyla).
 * Yeni kodda MAX_PRESENTATION_LAG_MS kullanılır.
 */
export const MAX_FRAMES_DROPPED = Math.round(MAX_PRESENTATION_LAG_MS / ASSUMED_FRAME_MS)

/** Bir gözlemde sunumda kaybedilen süreyi hesaplar */
export function presentationLagMs(obs: {
  framesDropped: number
  frameMs?: number
  lagMs?: number
}): number {
  if (obs.lagMs !== undefined) return obs.lagMs
  return obs.framesDropped * (obs.frameMs ?? ASSUMED_FRAME_MS)
}

/** Bir ölçümde geçersiz deneme oranı bunu aşarsa ölçüm bütün olarak şüpheli */
export const MAX_INVALID_RATIO = 0.15

export interface Judged {
  rtMs: number | null
  /** Sunumda kaybedilen süre — veritabanına da yazılır */
  lagMs: number
  outcome: Outcome | null
  correct: boolean
  valid: boolean
  invalidReason: InvalidReason | null
}

/**
 * Bir gözlemi değerlendirir: tepki süresi, sonuç türü, doğruluk, geçerlik.
 *
 * Sıralama önemli: önce geçerlik bakılır. Geçersiz bir denemenin "doğru"
 * sayılması merdiveni yanlış yönlendirir.
 */
export function judge(spec: TrialSpec, obs: TrialObservation): Judged {
  const rtMs = obs.responseAtMs === null ? null : obs.responseAtMs - obs.stimulusAtMs

  let valid = true
  let invalidReason: InvalidReason | null = null

  const lagMs = presentationLagMs(obs)

  if (obs.stalled) {
    // En öncelikli kontrol: kare akışı durduysa süreler zaten anlamsız
    valid = false
    invalidReason = 'presentation_stalled'
  } else if (obs.hidden) {
    valid = false
    invalidReason = 'tab_hidden'
  } else if (lagMs >= MAX_PRESENTATION_LAG_MS) {
    valid = false
    invalidReason = 'frames_dropped'
  } else if (rtMs !== null && rtMs < 0) {
    // Uyarıcıdan önce basılmış: anticipation. Eskiden input_lost diyorduk,
    // ama bu teknik bir arıza değil davranışsal bir bulgu — dürtüsellik.
    valid = false
    invalidReason = 'anticipation'
  } else if (rtMs !== null && rtMs < MIN_PLAUSIBLE_RT_MS) {
    valid = false
    invalidReason = 'too_fast'
  }

  const responded = obs.responseAtMs !== null
  let outcome: Outcome | null = null
  let correct = false

  switch (spec.expect) {
    case 'respond':
      outcome = responded ? 'hit' : 'miss'
      correct = responded
      break

    case 'withhold':
      // Ketleme denemesi: tepki vermemek DOĞRU cevaptır
      outcome = responded ? 'false_alarm' : 'correct_rejection'
      correct = !responded
      break

    case 'choice':
      if (!responded) {
        outcome = 'omission'
        correct = false
      } else {
        correct = sameAnswer(obs.response, spec.correct)
        outcome = correct ? 'hit' : 'false_alarm'
      }
      break

    case 'sequence':
      if (!responded) {
        outcome = 'omission'
        correct = false
      } else {
        correct = sameAnswer(obs.response, spec.correct)
        outcome = correct ? 'hit' : 'miss'
      }
      break

    /**
     * Zamanlama: cevap vuruş zamanları, doğruluk TOLERANSLI.
     *
     * Aralıklar kendi ortalamalarına bölünerek karşılaştırılıyor; yani
     * ritmi doğru ama yavaş tekrarlamak doğru sayılıyor. Mutlak
     * milisaniyeye bakmak, ritmi bilen ama yavaş çalan çocuğu yanlış
     * gösterirdi — ölçtüğümüz şey tempo değil, oran.
     */
    case 'timing': {
      const hedef = (spec.stimulus['araliklar'] as number[] | undefined) ?? []
      const tolerans = (spec.stimulus['tolerans'] as number | undefined) ?? 0.25
      const zamanlar = (Array.isArray(obs.response) ? obs.response : [])
        .map(Number)
        .filter((x) => Number.isFinite(x))
      if (!responded || zamanlar.length !== hedef.length + 1) {
        outcome = 'omission'
        correct = false
        break
      }
      const verilen: number[] = []
      for (let i = 1; i < zamanlar.length; i++) verilen.push(zamanlar[i]! - zamanlar[i - 1]!)
      if (verilen.some((d) => d <= 0)) {
        outcome = 'miss'
        correct = false
        break
      }
      const ortH = hedef.reduce((a, b) => a + b, 0) / hedef.length
      const ortV = verilen.reduce((a, b) => a + b, 0) / verilen.length
      const enKotu = Math.max(
        ...hedef.map((h, i) => Math.abs(verilen[i]! / ortV - h / ortH) / (h / ortH)),
      )
      correct = enKotu <= tolerans
      outcome = correct ? 'hit' : 'miss'
      break
    }
  }

  return { rtMs, lagMs, outcome, correct, valid, invalidReason }
}

function sameAnswer(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => String(x) === String(b[i]))
  }
  if (a === undefined || b === undefined) return false
  return String(a) === String(b)
}

/**
 * Ölçümün bütün olarak güvenilir olup olmadığı. Geçersiz deneme oranı
 * eşiği aşarsa rapor "bu ölçüm tekrarlanmalı" der — sayı üretip susmaz.
 */
export function assessmentValidity(trials: { valid: boolean }[]): {
  invalidRatio: number
  trustworthy: boolean
} {
  if (trials.length === 0) return { invalidRatio: 0, trustworthy: false }
  const invalid = trials.filter((t) => !t.valid).length
  const ratio = invalid / trials.length
  return { invalidRatio: ratio, trustworthy: ratio <= MAX_INVALID_RATIO }
}
